import React, { useCallback, useEffect, useState } from "react";
import { Nav, Row, Col, Tab } from "react-bootstrap";
import styled from "styled-components";
import { FaShoppingCart } from "react-icons/fa";
import { ImTree } from "react-icons/im";
import { RiSettings5Fill } from "react-icons/ri";
import { SiHomeassistantcommunitystore } from "react-icons/si";
import TabMenues from "./TabMenues";
import BuyTabs from "../buy-tab";
import DistributeTab from "../distribute-tab";
import ProductTab from "../product-tab";
import StoreTab from "../store-tab";
import { getProductBuys, getProductStatistics } from "network/apis/BuyApi";
import { getDistributes } from "network/apis/DistributeApi";
import { HDivider } from "components/CommonWidgets";
import { useQuery, useQueryClient } from "react-query";
import { useCompanySelector, useCompanyDispatch } from 'company-app/redux/company.store';
import { resetAllFilters } from 'company-app/redux/filter.slice';
import { getProducts } from "network/apis/ProductApi";
import { getStores } from "network/apis/StoreApi";
import useScreen from 'hooks/useScreen';

const MainCard = styled.div`
  background-color: #f6f6f6;
  position: relative;  
  border-top-left-radius: 36px;
  border-top-right-radius: 36px;
  margin: 5rem 0;
`;

const TabsCard = styled.div`
  background-color: white;
  position: absolute;
  width: 80%;
  top: -3rem;
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 33px;
  border: 1px solid #c6c6c6;
`;

const TabColumn = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  a {
    color: black;
    font-weight: 900;
    font-size: 14pt;
  }
`;

const TabLine = styled.div`
  width: 10em;
  height: 0.5rem;
  background-color: #0278ee;
  border-top-left-radius: 11px;
  border-top-right-radius: 11px;
`;

const TabContent = styled(Tab.Content)`
  width: 100%;
  margin: 14.5rem 0 20rem 0;
  padding: 0 ${(props) => props.x_padding} 0 ${(props) => props.x_padding};
  transform: translate(
    0,
    ${(props) => (props.t_offset ? props.t_offset : 0)}px
  );
  transition: all 500ms ease-in-out;
`;

/* 
props:
    parentType: which app is using this widget -> company, admin, province
*/
const HomeTabs = ({ tabContentXPadding = '1rem', parentType = 'company' }) => {
  const queryClient = useQueryClient();
  const filterState = useCompanySelector((store) => store.filter);
  const dispatch = useCompanyDispatch();

  const { lessThan1050, lessThan900, lessThan768, lessThan600 } = useScreen();

  const [currentTab, setCurrentTab] = useState("buy");
  const [currentTabData, setCurrentTabData] = useState();
  const [currentTopMargin, setCurrentTopMargin] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    let topOffset = 0;
    if (lessThan600) {
      topOffset = 380;
    }
    else if (lessThan768) {
      topOffset = 350;
    } else if (lessThan1050) {
      topOffset = 70;
    } else {
      topOffset = 0;
    }

    if (currentTab === 'store' && currentTopMargin > 0) {
      topOffset = topOffset / 1.9;
    }

    setCurrentTopMargin(topOffset);
  }, [currentTab, lessThan1050, lessThan768, lessThan600]);

  const toggleTopMargin = useCallback(
    (toggle) => {
      // let openOffset = lessThan768 ? 420 : 155;
      // let closeOffset = lessThan768 ? 350 : 0;

      let openOffset = 0;
      let closeOffset = 0;
      if (lessThan600) {
        openOffset = 850;
        closeOffset = 380;
      }
      else if (lessThan768) {
        openOffset = 830;
        closeOffset = 350;
      } else if (lessThan900) {
        openOffset = 600;
        closeOffset = 70;
      } else {
        openOffset = 155;
        closeOffset = 0;
      }

      setCurrentTopMargin(toggle ? openOffset : closeOffset);
    },
    [setCurrentTopMargin, lessThan600, lessThan768, lessThan900]
  );

  ///forTab = buy | dist | product | store
  const getFilterQuery = useCallback((forTab) => {
    let filter = '';
    switch (forTab) {
      case 'buy':
      case 'dist':
        filter = filterState.filterQuery;
        break;
      case 'product':
        filter = filterState.productsFilterQuery;
        break;
      case 'store':
        filter = filterState.storeFilterQuery;
        break;
    }    

    return parentType === 'admin' ? filter.replace('all_items=true', '') : filter;
  }, [parentType, filterState.filterQuery, filterState.productsFilterQuery, filterState.storeFilterQuery]);

  const {
    data: buysData,
    refetch: refetchBuys,
    isLoading: buysLoading,
  } = useQuery(
    ["company-get-product-buys", getFilterQuery('buy')],
    () => getProductBuys(getFilterQuery('buy')),
    {
      refetchOnWindowFocus: false,
      enabled: currentTab === 'buy',
    }
  );

  // setInterval(() => {
  //   console.log('INTERVAL -- going to invalidate query...');
  //   queryClient.invalidateQueries(["company-get-product-buys", getFilterQuery()]);
  // }, 5000);

  const {
    data: distsData,
    refetch: refetchDists,
    isLoading: distsLoading,
  } = useQuery(
    ["company-get-dists", getFilterQuery('dist')],
    () => getDistributes(getFilterQuery('dist')),
    {
      refetchOnWindowFocus: false,
      enabled: currentTab === 'distribute',
    }
  );

  const {
    data: productsData,
    refetch: refetchProducts,
    isLoading: productsLoading,
  } = useQuery(
    ["company-get-products", getFilterQuery('product')],
    () => getProducts(getFilterQuery('product')),
    {
      refetchOnWindowFocus: false,
      enabled: currentTab === 'product',
    }
  );

  const {
    data: storeData,
    refetch: refetchStore,
    isLoading: storesLoading,
  } = useQuery(
    ["company-get-stores", getFilterQuery('store')],
    () => getStores(getFilterQuery('store')),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    let statistics = storeData?.data?.statistics;
    if (statistics && currentTab === 'store') {
      statistics.sum += (storeData?.data?.concentrate?.sum ?? 0);
      setCurrentTabData(statistics);
    }
  }, [storeData?.data, currentTab]);

  const tabMenues = useCallback(
    () => (
      <TabMenues
        key={refreshCount}
        parentType={parentType}
        currentTab={currentTab}
        currentData={currentTabData}
        filterQuery={getFilterQuery('buy')}
        productsFilterQuery={getFilterQuery('product')}
        onCollapse={toggleTopMargin}
        onCreateSuccess={() => {
          if (currentTab === "buy") {
            refetchBuys();
            queryClient.invalidateQueries(['company-get-product-statistics']);
          }
          if (currentTab === "distribute") {
            refetchDists();
            queryClient.invalidateQueries(['get-dist-statistics']);
          }
          if (currentTab === "product") {
            refetchProducts();
            queryClient.invalidateQueries(['company-get-product-statistics']);
          }
        }}
      />
    ),
    [
      currentTabData,
      currentTab,
      toggleTopMargin,
      refetchBuys,
      refetchDists,
      refetchProducts,
      queryClient,
      refreshCount,
      getFilterQuery,
    ]
  );

  return (
    <MainCard>
      <Tab.Container
        defaultActiveKey={currentTab}
        onSelect={(k) => {
          dispatch(resetAllFilters());
          toggleTopMargin(false);
          setCurrentTabData(null);

          setCurrentTab(k);

          if (k === 'store') refetchStore();
        }}
      >
        <TabsCard className="pt-4">
          <Nav>
            <Row className="w-100">
              <TabColumn md={3}>
                <SiHomeassistantcommunitystore size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"store"}>انبار</Nav.Link>
                </Nav.Item>
                {currentTab === "store" && <TabLine />}
              </TabColumn>
              <TabColumn md={3}>
                <RiSettings5Fill size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"product"}>تولید</Nav.Link>
                </Nav.Item>
                {currentTab === "product" && <TabLine />}
              </TabColumn>
              <TabColumn md={3}>
                <ImTree size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"distribute"}>توزیع</Nav.Link>
                </Nav.Item>
                {currentTab === "distribute" && <TabLine />}
              </TabColumn>
              <TabColumn md={3}>
                <FaShoppingCart size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"buy"}>خرید</Nav.Link>
                </Nav.Item>
                {currentTab === "buy" && <TabLine />}
              </TabColumn>
            </Row>
            <HDivider />
          </Nav>
          {tabMenues()}
        </TabsCard>
        <Row>
          <TabContent t_offset={currentTopMargin} x_padding={tabContentXPadding}>
            <Tab.Pane eventKey="buy">
              <BuyTabs
                buysData={buysData}
                isLoading={buysLoading}
                refetch={refetchBuys}
                onEditTap={(editData) => {
                  setCurrentTabData(editData);
                  toggleTopMargin(true);
                }}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="distribute">
              <DistributeTab
                parentType={parentType}
                distsData={distsData}
                isLoading={distsLoading}
                refetch={refetchDists}
                onEditTap={(editData) => {
                  setCurrentTabData(editData);
                  toggleTopMargin(true);
                }}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="product">
              <ProductTab
                productsData={productsData}
                isLoading={productsLoading}
                refetch={refetchProducts}
                onEditTap={(editData) => {
                  setCurrentTabData(editData);
                  setRefreshCount((prev) => prev + 1);
                }}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="store">
              <StoreTab
                storeData={storeData}
                isLoading={storesLoading}
                parentType={parentType}
              />
            </Tab.Pane>
          </TabContent>
        </Row>
      </Tab.Container>
    </MainCard>
  );
};

export default HomeTabs;
